import React, { useEffect, useRef } from 'react';
import { Transition, TransitionChild } from '@headlessui/react';
import useClassNames from 'helpers/hooks/useClassNames';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useOnClickOutside from 'helpers/hooks/useOnClickOutside';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import { desktop } from 'helpers/utils/screensizes';
import Overlay from '../overlay';
import useSwipeToDismiss from './useSwipeToDismiss';

export interface DrawerProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
  isOpen: boolean;
  direction?: 'left' | 'top' | 'right' | 'bottom';
  blockScrolling?: boolean;
  onClose?: () => void;
  role?: string;
  isDisableCartOverlay?: boolean;
  /**
   * Allow a downward drag to dismiss the drawer. Bottom drawers only. Mark the
   * grab handle with `data-drawer-drag-handle` so it always starts a drag, and
   * any subtree that must never start one with `data-drawer-no-swipe`.
   */
  swipeToClose?: boolean;
}

const Drawer = ({
  className = '',
  isOpen,
  direction = 'right',
  blockScrolling = true,
  onClose,
  children,
  isDisableCartOverlay = false,
  swipeToClose = false,
  ...rest
}: React.PropsWithChildren<DrawerProps>) => {
  const { blockScroll } = useScrollBlock();
  const [isDesktopSize] = useMediaQuery(desktop);

  useEffect(() => {
    if (blockScrolling) {
      blockScroll(isOpen);
    }
    return () => {
      if (blockScrolling) {
        blockScroll(false);
      }
    };
  }, [isOpen, blockScroll, blockScrolling]);

  useEffect(() => {
    if (isOpen) {
      ref.current?.focus();
    }
  }, [isOpen]);
  // focus trap
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const container = ref.current;
      if (!container) return;

      const focusable = container.querySelectorAll<HTMLElement>('a, button, input, textarea, select, [tabindex="0"]');

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // ESC close
  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    document.addEventListener('keydown', handleEsc);

    return () => {
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  const ref = useRef<HTMLDivElement>(null);

  const { panelRef, swipeHandlers } = useSwipeToDismiss({
    enabled: swipeToClose && direction === 'bottom' && isOpen,
    onDismiss: onClose,
  });

  useOnClickOutside(ref, () => {
    if (isOpen && onClose) {
      onClose();
    }
  });

  const directionStyles = {
    left: 'left-0 top-0 h-full',
    top: 'left-0 top-0 w-full',
    right: 'right-0 top-0 h-full',
    bottom: 'left-0 bottom-0 w-full',
  };

  const drawerTransitions = {
    left: {
      enterFrom: '-translate-x-full transform opacity-0',
      enterTo: 'translate-x-0 opacity-100 transition',
      leaveFrom: 'translate-x-0 opacity-100 transition',
      leaveTo: '-translate-x-full opacity-0 transition',
    },
    top: {
      enterFrom: '-translate-y-full transform opacity-0',
      enterTo: 'translate-y-0 opacity-100 transition',
      leaveFrom: 'translate-y-0 opacity-100 transition',
      leaveTo: '-translate-y-full opacity-0 transition',
    },
    right: {
      enterFrom: 'translate-x-full transform opacity-0',
      enterTo: '-translate-x-0 opacity-100 transition',
      leaveFrom: '-translate-x-0 opacity-100 transition',
      leaveTo: 'translate-x-full opacity-0 transition',
    },
    bottom: {
      enterFrom: 'translate-y-full transform opacity-0',
      enterTo: '-translate-y-0 opacity-100 transition',
      leaveFrom: '-translate-y-0 opacity-100 transition',
      leaveTo: 'translate-y-full opacity-0 transition',
    },
  };

  const drawerClassName = useClassNames([className, 'fixed z-[999] shadow-lg', directionStyles[direction]]);

  return (
    <>
      {isOpen && !isDisableCartOverlay && <Overlay />}

      <Transition show={isOpen}>
        <TransitionChild
          as="div"
          enter="ease-out"
          enterFrom={drawerTransitions[direction].enterFrom}
          enterTo={drawerTransitions[direction].enterTo}
          leave="ease-in duration-75"
          leaveFrom={drawerTransitions[direction].leaveFrom}
          leaveTo={drawerTransitions[direction].leaveTo}
          className={drawerClassName}
          ref={panelRef}
          {...swipeHandlers}
          {...rest}
        >
          <div
            ref={ref}
            tabIndex={-1}
            className={
              isDisableCartOverlay
                ? `max-h-screen overflow-auto md:max-h-[80vh] ${isDesktopSize && 'shadow-lg'} `
                : 'h-full'
            }
          >
            <div className="flex h-full flex-col items-stretch">{children}</div>
          </div>
        </TransitionChild>
      </Transition>
    </>
  );
};

export default Drawer;
